<template>
  <!-- Overview -->
  <div class="o-layout o-overview">
    <div class="o-overview__main-search u-col-start:1 u-col-end:13">
      <form @submit.prevent="debouncedResetAndLoad" class="m-filter-search">
        <label
          for="search-teachers"
          class="u-screen-reader-only"
          v-text="$t('teachers_overview.search')"
        ></label>
        <input
          class="m-filter-search__input"
          type="text"
          id="search-teachers"
          :placeholder="$t('teachers_overview.search')"
          v-model="search"
          @input="debouncedResetAndLoad"
        />

        <button class="m-filter-search__btn" type="submit">
          <svg aria-hidden="true">
            <use :xlink:href="$root.spritePath + '#search'" />
          </svg>
        </button>
      </form>

      <span class="o-overview__results-count a-h4">
        {{ total }} {{ total !== 1 ? $t('teachers_overview.results') : $t('teachers_overview.result') }}
      </span>
    </div>

    <!-- Main -->
    <section
      class="o-overview__main u-col-start:1@lg u-col-end:13@lg"
      aria-labelledby="teachers-title"
    >
      <h2
        id="teachers-title"
        class="u-screen-reader-only"
        v-text="$t('teachers_overview.teachers')"
      ></h2>

      <main
        class="o-overview__main-content o-layout"
        id="teachers-overview"
      >
        <!-- Loading -->
        <template v-if="loading">
          <div class="u-col-span:6@md" v-for="(item, index) in Array(12)" :key="index">
            <div class="m-tile m-tile--loading"></div>
          </div>
        </template>

        <!-- Error after loading -->
        <template v-else-if="error">
          <div class="u-col-span:12 m-paragraph o-editor-content">
            <p>
              {{ $t('teachers_overview.loading_error') }}
            </p>
          </div>
        </template>

        <!-- No results -->
        <template v-else-if="items.length === 0">
          <div class="u-col-span:12 m-paragraph o-editor-content">
            <p>
              {{ $t('teachers_overview.no_content') }}
            </p>
          </div>
        </template>

        <!-- Items -->
        <template v-else>
          <template v-for="entry in items" :key="entry.id">

            <!-- Tile -->
            <div class="u-col-span:4@md">
              <a :href="entry.url" class="m-tile">
                <picture v-if="entry.image" class="a-image m-tile__image">
                  <source :data-srcset="entry.image.srcset" />
                  <img
                    class="a-image__img"
                    :data-src="entry.image.src"
                    :alt="entry.image.alt"
                    loading="lazy"
                  />
                </picture>
                <picture v-else class="a-image m-tile__image">
                  <svg class="a-image__img" aria-hidden="true">
                    <use :xlink:href="$root.spritePath + '#placeholder-landscape'"/>
                  </svg>
                </picture>

                <div class="m-tile__header">
                  <svg class="m-tile__arrow" aria-hidden="true">
                    <use :xlink:href="$root.spritePath + '#btn-arrow-right'"/>
                  </svg>
                  <h3
                    class="a-h3 m-tile__title"
                    v-text="entry.title"
                  ></h3>
                </div>

                <div
                  v-if="entry.disciplines.length"
                  class="m-tile__subtitle"
                  v-text="entry.disciplines.join(', ')"
                ></div>

                <div
                  v-if="entry.intro_text"
                  class="m-tile__main"
                  v-html="entry.intro_text"
                ></div>
              </a>
            </div>
          </template>
        </template>
      </main>

      <footer v-if="totalPages > 1" class="o-overview__main-footer">
        <BasePagination
          v-model="filterPage"
          :total="total"
          :total-pages="totalPages"
          @update:modelValue=";[scrollToTop(), load()]"
        ></BasePagination>
      </footer>
    </section>
  </div>
</template>

<script>
import BasePagination from './BasePagination.vue'
import axios from 'axios'
import { debounce } from 'vue-debounce'
import { vElementVisibility } from '@vueuse/components'

export default {
  directives: {
    ElementVisibility: vElementVisibility,
  },

  components: {
    BasePagination,
  },

  data: () => ({
    items: [],
    filters: null,
    total: "…",
    totalPages: null,

    search: "",
    filterPage: 1,

    controller: null,
    loading: true,
    error: false,
  }),

  async mounted() {
    this.loadParams()
    this.load()
  },

  methods: {
    loadParams() {
      const params = new URLSearchParams(window.location.search)

      if (params.get("q")) {
        this.search = params.get("q")
      }

      if (params.get("page")) {
        this.filterPage = parseInt(params.get("page"), 10)
      }
    },

    updateParams() {
      const params = new URLSearchParams(window.location.search)

      this.search !== ""
        ? params.set("q", this.search)
        : params.delete("q")

      this.filterPage !== 1
        ? params.set("page", this.filterPage)
        : params.delete("page")

      // Update state without adding history.
      const newParams = params.toString()

      newParams === ""
        ? history.replaceState({}, null, window.location.pathname)
        : history.replaceState({}, null, window.location.pathname + "?" + newParams)
    },

    setPage(page) {
      // Scroll to top of the page when the page differs.
      if (this.filterPage !== page) {
        this.scrollToTop()
      }

      this.filterPage = page
    },

    scrollToTop() {
      const duration = 500,
            bodyRect = document.body.getBoundingClientRect(),
            elemRect = document.body.querySelector('#content').getBoundingClientRect(),
            startingY = window.scrollY,
            offset = elemRect.top - bodyRect.top,
            diff = offset - startingY

      let start

      // Bootstrap our animation - it will get called right before next frame shall be rendered.
      window.requestAnimationFrame(function step(timestamp) {
        if (!start) start = timestamp;
        // Elapsed milliseconds since start of scrolling.
        const time = timestamp - start;
        // Get percent of completion in range [0, 1].
        const percent = Math.min(time / duration, 1);

        window.scrollTo(0, startingY + diff * percent);

        // Proceed with animation as long as we wanted it to.
        if (time < duration) {
          window.requestAnimationFrame(step);
        }
      })
    },

    async load() {
      let loadingTimeout = setTimeout(() => {
        this.loading = true
        this.error = false
      }, 600)

      if (this.controller) {
        this.controller.abort()
      }

      try {
        this.controller = new AbortController()

        const response = await axios.get(window.siteUrl.replace(/\/$/, '') + "/api/v1/teachers", {
          signal: this.controller.signal,
          params: {
            q: this.search,
            page: this.filterPage,
          },
        })

        this.items = response.data.entries
        this.filters = response.data.filters
        this.total = response.data.pagination.total
        this.totalPages = response.data.pagination.total_pages

        this.updateParams()
      } catch (e) {
        this.total = 0;
        this.error = true;
      } finally {
        clearTimeout(loadingTimeout)

        this.loading = false
        this.$nextTick(() => this.$root.lazyLoad.update())
      }
    },

    debouncedResetAndLoad: debounce(async function() {
      this.setPage(1)
      await this.load()
    }, 400),
  },
}
</script>
